import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { OrderStatus as OrderStatusEnum, getOrderStatusText } from '../../../../../models/enums/OrderStatus';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { dashboardStore } from '../../../../../app/stores/Dashboard';
import PortalCard from '../../../../../components/PortalCard';

interface StatusData {
    status: OrderStatusEnum;
    name: string;
    count: number;
    color: string;
}

interface OrderStatusCardProps {
    title?: string;
}

const OrderStatusCard = ({
    title = 'Order Statuses',
}: OrderStatusCardProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { orderStatuses } = dashboardStore(state => ({ orderStatuses: state.orderStatuses }));

    // Prepare order status data
    const getOrderStatusData = (): StatusData[] => {
        const statusColors = {
            [OrderStatusEnum.PENDING]: '#f97316', // Orange
            [OrderStatusEnum.PROCESSING]: '#3730a3', // Purple
            [OrderStatusEnum.SHIPPED]: '#38bdf8', // Blue
            [OrderStatusEnum.DELIVERED]: '#34d399', // Green
        };

        // Create an array with all possible statuses, even if they have 0 count
        const allStatuses = [
            OrderStatusEnum.PENDING,
            OrderStatusEnum.PROCESSING,
            OrderStatusEnum.SHIPPED,
            OrderStatusEnum.DELIVERED
        ];

        // Map our real order status data
        const existingStatusMap = new Map(
            orderStatuses.map(item => [item.status, item.status_count])
        );

        return allStatuses.map(statusValue => ({
            status: statusValue,
            name: getOrderStatusText(statusValue),
            count: existingStatusMap.get(statusValue) || 0,
            color: statusColors[statusValue]
        }));
    };

    // Calculate total orders from order statuses
    const getTotalOrdersFromStatuses = (): number => {
        const total = orderStatuses.reduce((sum, item) => sum + item.status_count, 0);
        return total > 0 ? total : 0;
    };

    // Get statuses data
    const statuses = getOrderStatusData();
    const totalOrders = getTotalOrdersFromStatuses();
    const chartHeight = isMobile ? 120 : 160;

    // Prepare data for the PieChart
    const renderPieChart = () => {
        if (statuses.length === 0 || totalOrders === 0) {
            return (
                <Box
                    sx={{
                        height: chartHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography
                        variant='body2'
                        color='text.secondary'
                        fontFamily='primaryFont'
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                        No data available
                    </Typography>
                </Box>
            );
        }

        // Filter out statuses with 0 count to avoid empty segments
        const chartData = statuses
            .filter(status => status.count > 0)
            .map(status => ({
                id: status.status,
                value: status.count,
                label: status.name,
                color: status.color,
            }));

        return (
            <Box sx={{ height: chartHeight, width: '100%' }}>
                <PieChart
                    series={[
                        {
                            arcLabel: (item) => `${item.value}`,
                            arcLabelMinAngle: 45,
                            data: chartData,
                            highlightScope: { faded: 'global', highlighted: 'item' },
                            faded: { innerRadius: 20, additionalRadius: -20, color: 'gray' },
                            innerRadius: isMobile ? 20 : 30,
                            paddingAngle: 2,
                            cornerRadius: 4,
                        },
                    ]}
                    sx={{
                        [`& .${pieArcLabelClasses.root}`]: {
                            fill: theme.palette.text.primary,
                            fontWeight: 'bold',
                            fontSize: isMobile ? 10 : 12,
                        },
                    }}
                    height={chartHeight}
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    slotProps={{
                        legend: { hidden: true }
                    }}
                />
            </Box>
        );
    };

    return (
        <PortalCard
            sx={{
                p: { xs: 1, sm: 2 },
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {/* Header */}
            <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                <Typography
                    variant='h6'
                    fontWeight='bold'
                    fontFamily='primaryFont'
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                    {title}
                </Typography>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    fontFamily='primaryFont'
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                    Total Orders: {totalOrders}
                </Typography>
            </Box>

            {/* Pie chart */}
            {renderPieChart()}

            {/* Status Breakdown */}
            <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                <Typography
                    variant='subtitle2'
                    sx={{
                        mb: { xs: 1.5, sm: 2 },
                        fontWeight: 'bold',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                    fontFamily='primaryFont'
                >
                    Status Breakdown
                </Typography>

                {/* Status list */}
                {statuses.length === 0 ? (
                    <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                        No order data available
                    </Typography>
                ) : (
                    <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: { xs: 1, sm: 1.5 },
                            mb: { xs: 1.5, sm: 3 },
                        }}>
                            {statuses.map((status, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        p: { xs: 1, sm: 1.5 },
                                        borderLeft: `3.5px solid ${status.color}`,
                                        '&:hover': {
                                            borderLeft: `6px solid ${status.color}`,
                                            transition: 'border 0.2s ease-in-out',
                                        },
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.03)'
                                            : 'rgba(0, 0, 0, 0.02)',
                                        borderRadius: 4,
                                    }}
                                >
                                    <Typography
                                        variant='h6'
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '1rem', sm: '1.25rem' },
                                            lineHeight: 1,
                                        }}
                                        fontFamily='primaryFont'
                                    >
                                        {status.count}
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        fontFamily='primaryFont'
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                            lineHeight: 1.2,
                                            mb: 0.25,
                                        }}
                                    >
                                        {status.name}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default OrderStatusCard; 